import { normalizeLangTag } from '../lang.ts';
import { normalizeUiLocale, type UiLocaleId } from './locale.ts';

/**
 * **The 49 UI languages the instance itself offers**, harvested from its own `/preferences`
 * `<select name="locale">` on 2026-07-31 — the way `CONTENT_REGIONS` and `INVIDIOUS_DASH_HEIGHTS` were.
 *
 * Measured rather than assumed, because three of the rows would have been got wrong by guessing:
 *
 * - **English is `en-US`, not `en`.** A push writing `en` stores a value Invidious's own preferences
 *   page then cannot show as selected — the endpoint validates nothing (§8d finding 4), so it would
 *   look like a working write that quietly emptied the account's language dropdown.
 * - **`sr_Cyrl` uses an underscore**, which is not BCP-47 at all. It is the one tag in the set that a
 *   naive comparison against a browser's `navigator.languages` cannot match; `normalizeLangTag` folds
 *   `_` to `-`, which is why the fold exists rather than being tidiness.
 * - Portuguese appears **three** times (`pt`, `pt-BR`, `pt-PT`) and Chinese twice (`zh-CN`, `zh-TW`),
 *   so "one language, one tag" does not hold and a reverse lookup must be primary-subtag-based.
 *
 * The native names are the instance's own option labels. They are here for one reason and it is not
 * decoration: when the account's language is one this build has no copy for, the settings page can say
 * *"the account is set to Deutsch and Visious will leave it that way"* instead of printing `de` and
 * leaving the reader to wonder whether a push is about to overwrite it (`settings/sync.ts`'s one rule,
 * made visible).
 */
export interface InvidiousLocale {
  /** Exactly as the instance spells it — including `en-US` and the underscore in `sr_Cyrl`. */
  readonly tag: string;
  /** The instance's own label for it, in that language. */
  readonly name: string;
}

export const INVIDIOUS_LOCALES: readonly InvidiousLocale[] = [
  { tag: 'ar', name: 'العربية' },
  { tag: 'bg', name: 'български' },
  { tag: 'bn', name: 'বাংলা' },
  { tag: 'ca', name: 'Català' },
  { tag: 'cs', name: 'Čeština' },
  { tag: 'cy', name: 'Cymraeg' },
  { tag: 'da', name: 'Dansk' },
  { tag: 'de', name: 'Deutsch' },
  { tag: 'el', name: 'Ελληνικά' },
  { tag: 'en-US', name: 'English' },
  { tag: 'eo', name: 'Esperanto' },
  { tag: 'es', name: 'Español' },
  { tag: 'et', name: 'Eesti keel' },
  { tag: 'eu', name: 'Euskara' },
  { tag: 'fa', name: 'فارسی' },
  { tag: 'fi', name: 'Suomi' },
  { tag: 'fr', name: 'Français' },
  { tag: 'he', name: 'עברית' },
  { tag: 'hi', name: 'हिन्दी' },
  { tag: 'hr', name: 'Hrvatski' },
  { tag: 'hu-HU', name: 'Magyar Nyelv' },
  { tag: 'id', name: 'Bahasa Indonesia' },
  { tag: 'is', name: 'Íslenska' },
  { tag: 'it', name: 'Italiano' },
  { tag: 'ja', name: '日本語' },
  { tag: 'ko', name: '한국어' },
  { tag: 'lmo', name: 'Lombard' },
  { tag: 'lt', name: 'Lietuvių' },
  { tag: 'nb-NO', name: 'Norsk bokmål' },
  { tag: 'nl', name: 'Nederlands' },
  { tag: 'pl', name: 'Polski' },
  { tag: 'pt', name: 'Português' },
  { tag: 'pt-BR', name: 'Português Brasileiro' },
  { tag: 'pt-PT', name: 'Português de Portugal' },
  { tag: 'ro', name: 'Română' },
  { tag: 'ru', name: 'Русский' },
  { tag: 'si', name: 'සිංහල' },
  { tag: 'sk', name: 'Slovenčina' },
  { tag: 'sl', name: 'Slovenščina' },
  { tag: 'sq', name: 'Shqip' },
  { tag: 'sr', name: 'Srpski (latinica)' },
  { tag: 'sr_Cyrl', name: 'Српски (ћирилица)' },
  { tag: 'sv-SE', name: 'Svenska' },
  { tag: 'ta', name: 'தமிழ்' },
  { tag: 'tr', name: 'Türkçe' },
  { tag: 'uk', name: 'Українська' },
  { tag: 'vi', name: 'Tiếng Việt' },
  { tag: 'zh-CN', name: '汉语' },
  { tag: 'zh-TW', name: '漢語' },
];

/**
 * The tag to write for a language this build renders in, or `null` when the instance has no option
 * for it — in which case the field is **omitted from the push** and the account keeps what it had.
 *
 * Primary-subtag matched, and the first match wins, which is what makes `pt` rather than `pt-BR` the
 * answer for a hypothetical Portuguese catalogue: the unqualified option is the one Invidious lists
 * first and the one that claims the least.
 */
export function invidiousLocaleTagFor(ui: UiLocaleId): string | null {
  const match = INVIDIOUS_LOCALES.find((locale) => normalizeUiLocale(locale.tag) === ui);
  return match?.tag ?? null;
}

/**
 * The instance's own label for a stored tag, or `null` if it is not one of the 49.
 *
 * `null` is a real answer rather than a lookup miss: the endpoint stores `locale: "xx-XX"` happily
 * (§8d finding 4), so an account can genuinely hold a language its own UI cannot show either.
 */
export function invidiousLocaleName(tag: string | null): string | null {
  if (tag === null) return null;
  const wanted = normalizeLangTag(tag);
  if (wanted === '') return null;
  const match = INVIDIOUS_LOCALES.find((locale) => normalizeLangTag(locale.tag) === wanted);
  return match?.name ?? null;
}
